import React, { useState } from 'react';
import { User, Zap, Building2, ArrowRight, UserCheck, ChevronsUpDown, Check, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from '../../components/ui/command';
import { HighTechCard } from './shared';
import { cn } from '../../lib/utils';

export default function StepMemberSelection({ ctx }) {
  const { 
    caHT, montantTotalHT, montantTTC, membresAdherents,
    selectedClient, handleSelectMembre, setEtape,
    formData, isNominative, typeSouscription: typeSouscription
  } = ctx;

  const [open, setOpen] = useState(false);

  const membreNom = formData?.raison_sociale || formData?.entreprise || '';
  const membrePrenom = formData?.prenom_gerant || '';
  const membreNomGerant = formData?.nom_gerant || '';
  const membreSiret = formData?.siret || '';
  const membreTerritoire = formData?.territoire || '';
  const displayName = membreNom || [membrePrenom, membreNomGerant].filter(Boolean).join(' ') || '';
  const hasSelectedMembre = selectedClient && selectedClient !== 'nouveau' && displayName;

  const selectedLabel = selectedClient === 'nouveau'
    ? 'Creer un nouveau membre'
    : displayName || 'Selectionnez un membre...';

  const onSelect = (value) => {
    handleSelectMembre(value);
    setOpen(false);
  };

  return (
    <HighTechCard gradient="cyan">
      <div className="p-1">
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-t-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur">
              <User className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Selection du membre</h2>
              <p className="text-cyan-100 text-sm">Choisissez ou creez un membre adherent</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6 bg-white rounded-b-xl">
          {/* Banniere membre selectionne */}
          {hasSelectedMembre && (
            <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200" data-testid="membre-selectionne-banner">
              <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
                <UserCheck className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">
                  Simulation {isNominative ? 'nominative' : 'annuelle'} en cours pour
                </p>
                <p className="text-lg font-bold text-emerald-900" data-testid="membre-nom-display">
                  {displayName}
                </p>
                {(membrePrenom || membreNomGerant) && (
                  <p className="text-sm text-emerald-700">
                    Gerant : {membrePrenom} {membreNomGerant}
                  </p>
                )}
                <div className="flex items-center gap-4 mt-1 text-xs text-emerald-600">
                  {membreSiret && <span data-testid="membre-siret-display">SIRET : {membreSiret}</span>}
                  {membreTerritoire && <span data-testid="membre-territoire-display">Territoire : {membreTerritoire}</span>}
                </div>
              </div>
            </div>
          )}

          <div className="p-5 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 text-white">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-5 w-5 text-yellow-400" />
              <span className="font-bold">Tarification calculee</span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center p-3 rounded-lg bg-white/10">
                <p className="text-xs text-slate-400 uppercase">CA</p>
                <p className="text-lg font-bold text-cyan-400">{parseFloat(caHT || 0).toLocaleString('fr-FR')} &euro;</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/10">
                <p className="text-xs text-slate-400 uppercase">Montant de base</p>
                <p className="text-lg font-bold text-blue-400">{montantTotalHT?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} &euro;</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/10">
                <p className="text-xs text-slate-400 uppercase">Contributions solidaires</p>
                <p className="text-lg font-bold text-purple-400">{(montantTTC - montantTotalHT)?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} &euro;</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600">
                <p className="text-xs text-green-100 uppercase">Montant Global</p>
                <p className="text-xl font-black text-white" data-testid="step1-montant-global">{montantTTC?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} &euro;</p>
              </div>
            </div>
          </div>

          {/* Combobox recherche membre */}
          <div className="space-y-3">
            <Label className="text-base font-bold text-slate-700">Membre adherent</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  data-testid="membre-combobox-trigger"
                  className="w-full h-14 justify-between text-base border-2 border-slate-200 hover:border-blue-400 transition-colors bg-white text-left font-normal"
                >
                  <div className="flex items-center gap-3 truncate">
                    {selectedClient === 'nouveau' ? (
                      <div className="p-1.5 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 shrink-0">
                        <User className="h-3.5 w-3.5 text-white" />
                      </div>
                    ) : hasSelectedMembre ? (
                      <Building2 className="h-4 w-4 text-blue-500 shrink-0" />
                    ) : (
                      <Search className="h-4 w-4 text-slate-400 shrink-0" />
                    )}
                    <span className={cn("truncate", !selectedClient && "text-muted-foreground")}>
                      {selectedLabel}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Rechercher par nom, SIRET..." data-testid="membre-search-input" />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>Aucun membre trouve.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="nouveau-membre"
                        onSelect={() => onSelect('nouveau')}
                        className="py-3"
                        data-testid="membre-option-nouveau"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <div className="p-1.5 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 shrink-0">
                            <User className="h-3.5 w-3.5 text-white" />
                          </div>
                          <span className="font-semibold">Creer un nouveau membre</span>
                          {selectedClient === 'nouveau' && <Check className="ml-auto h-4 w-4 text-blue-600" />}
                        </div>
                      </CommandItem>
                    </CommandGroup>
                    <CommandSeparator />
                    <CommandGroup heading={`${membresAdherents.length} membres`}>
                      {membresAdherents.filter(m => m && m.id).map((membre) => {
                        const label = String(membre.raison_sociale || membre.company_name || 'N/A');
                        const siret = membre.siret || '';
                        return (
                          <CommandItem
                            key={String(membre.id)}
                            value={`${label} ${siret}`}
                            onSelect={() => onSelect(String(membre.id))}
                            className="py-2.5"
                            data-testid={`membre-option-${membre.id}`}
                          >
                            <div className="flex items-center gap-2 w-full min-w-0">
                              <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                              <div className="truncate flex-1">
                                <span className="font-medium">{label}</span>
                                {siret && <span className="ml-2 text-xs text-slate-400">{siret}</span>}
                              </div>
                              {selectedClient === String(membre.id) && <Check className="ml-auto h-4 w-4 text-blue-600 shrink-0" />}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex justify-end pt-4">
            <Button onClick={() => setEtape(2)} className="h-12 px-8 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 shadow-lg shadow-blue-500/30">
              Continuer <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </HighTechCard>
  );
}
